/**
 * Centralized validation/normalization for media uploads (images + videos).
 *
 * Browsers report inconsistent MIME types for some formats — most notably
 * `.mov` files, which surface as `video/quicktime` on macOS but as an empty
 * string or `application/octet-stream` on Windows/Linux. Strict MIME-only
 * checks reject the file silently. We fall back to file-extension sniffing
 * and re-wrap the File so the multipart upload sends a Content-Type the
 * server can validate.
 *
 * Keep the allow-lists below in lock-step with the server's
 * `ALLOWED_VIDEO_TYPES` / `ALLOWED_IMAGE_TYPES` in storage.service.ts.
 */

export const ALLOWED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heif',
  'image/heic',
] as const;

export const ALLOWED_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/quicktime',     // .mov
  'video/x-msvideo',     // .avi
  'video/webm',
  'video/x-matroska',    // .mkv
  'video/hevc',          // HEVC/H.265
  'video/x-m4v',         // .m4v
  'video/3gpp',          // .3gp
  'video/3gpp2',         // .3g2
  'video/mp2t',          // .ts/.mts
  'video/ogg',           // .ogv
] as const;

const VIDEO_EXTENSION_TO_MIME: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/x-m4v',
  mov: 'video/quicktime',
  qt: 'video/quicktime',
  avi: 'video/x-msvideo',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  hevc: 'video/hevc',
  '3gp': 'video/3gpp',
  '3g2': 'video/3gpp2',
  ts: 'video/mp2t',
  mts: 'video/mp2t',
  m2ts: 'video/mp2t',
  ogv: 'video/ogg',
};

const IMAGE_EXTENSION_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};

export const VIDEO_FILE_EXTENSIONS = Object.keys(VIDEO_EXTENSION_TO_MIME).map(e => `.${e}`);
export const IMAGE_FILE_EXTENSIONS = Object.keys(IMAGE_EXTENSION_TO_MIME).map(e => `.${e}`);

/**
 * The HTML `accept` attribute for an image+video picker. Includes both the
 * generic `image/*`/`video/*` wildcards and explicit extensions, because some
 * platforms (notably Windows) don't surface `.mov` under `video/*`.
 */
export const MEDIA_ACCEPT_ATTRIBUTE = [
  'image/*',
  'video/*',
  ...IMAGE_FILE_EXTENSIONS,
  ...VIDEO_FILE_EXTENSIONS,
].join(',');

function getExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  if (idx === -1 || idx === filename.length - 1) return '';
  return filename.slice(idx + 1).toLowerCase();
}

export type MediaKind = 'image' | 'video' | 'unknown';

export interface ClassifiedMediaFile {
  kind: MediaKind;
  /** A canonical MIME type the server will accept, derived from MIME+extension. */
  normalizedMime: string;
  /**
   * The original file if its MIME was already canonical, otherwise a new File
   * with the same bytes and name but the canonical MIME stamped on it. Always
   * use this for upload — it ensures multipart Content-Type is a value the
   * server allow-lists.
   */
  file: File;
}

/**
 * Classify a user-picked file as image / video / unknown, normalizing the
 * MIME type via extension fallback if the browser provided a useless value.
 */
export function classifyMediaFile(file: File): ClassifiedMediaFile {
  const reportedMime = (file.type || '').toLowerCase();
  const ext = getExtension(file.name);

  const imageMimeFromExt = IMAGE_EXTENSION_TO_MIME[ext];
  const videoMimeFromExt = VIDEO_EXTENSION_TO_MIME[ext];

  const reportedIsImage = (ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(reportedMime);
  const reportedIsVideo = (ALLOWED_VIDEO_MIME_TYPES as readonly string[]).includes(reportedMime);

  let kind: MediaKind = 'unknown';
  let normalizedMime = reportedMime;

  if (reportedIsImage) {
    kind = 'image';
    normalizedMime = reportedMime;
  } else if (reportedIsVideo) {
    kind = 'video';
    normalizedMime = reportedMime;
  } else if (videoMimeFromExt) {
    kind = 'video';
    normalizedMime = videoMimeFromExt;
  } else if (imageMimeFromExt) {
    kind = 'image';
    normalizedMime = imageMimeFromExt;
  }

  const needsRewrap = kind !== 'unknown' && reportedMime !== normalizedMime;
  const outFile = needsRewrap
    ? new File([file], file.name, { type: normalizedMime, lastModified: file.lastModified })
    : file;

  return { kind, normalizedMime, file: outFile };
}

/** Convenience: true if the file resolves to either an image or a video. */
export function isAcceptedMediaFile(file: File): boolean {
  return classifyMediaFile(file).kind !== 'unknown';
}

/**
 * Server-aligned upload error codes — keep in lock-step with
 * `UPLOAD_ERROR_CODES` in storage.service.ts on the backend. Branching on
 * `code` is more reliable than parsing error strings.
 */
export const UPLOAD_ERROR_CODE = {
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  UNSUPPORTED_MIME_TYPE: 'UNSUPPORTED_MIME_TYPE',
  NO_FILE: 'NO_FILE',
  STORAGE_ERROR: 'STORAGE_ERROR',
  INTERNAL_ERROR: 'INTERNAL_ERROR'
} as const;

export type UploadErrorCode = typeof UPLOAD_ERROR_CODE[keyof typeof UPLOAD_ERROR_CODE];

interface ApiErrorLike {
  message?: string;
  status?: number;
  data?: {
    error?: string;
    code?: string;
    details?: {
      maxSize?: number;
      receivedSize?: number;
      allowedTypes?: string[];
      receivedType?: string;
    };
  };
}

/**
 * Translate an upload failure (raw `ApiError` from `lib/api.ts` or a string
 * code) into a short, user-facing message. Falls back to the server's text
 * when we don't recognize the code so we never swallow useful detail.
 */
export function describeUploadError(err: ApiErrorLike | undefined, fileName?: string): string {
  if (!err) return 'Upload failed. Please try again.';
  const namePrefix = fileName ? `"${fileName}": ` : '';

  const code = err.data?.code as UploadErrorCode | undefined;
  const details = err.data?.details;

  if (code === UPLOAD_ERROR_CODE.FILE_TOO_LARGE || err.status === 413) {
    if (details?.maxSize) {
      const maxMb = Math.round(details.maxSize / 1024 / 1024);
      const receivedMb = details.receivedSize
        ? ` (${(details.receivedSize / 1024 / 1024).toFixed(1)}MB)`
        : '';
      return `${namePrefix}File is too large${receivedMb}. Maximum size is ${maxMb}MB.`;
    }
    return `${namePrefix}File is too large.`;
  }

  if (code === UPLOAD_ERROR_CODE.UNSUPPORTED_MIME_TYPE || err.status === 415) {
    if (details?.receivedType) {
      return `${namePrefix}File type "${details.receivedType}" is not supported.`;
    }
    return `${namePrefix}File type is not supported.`;
  }

  if (code === UPLOAD_ERROR_CODE.NO_FILE) {
    return 'No file was provided.';
  }

  // Storage / internal / network errors — surface backend wording when present.
  const message = err.data?.error || err.message || 'Upload failed.';
  return `${namePrefix}${message}`;
}
